import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import confetti from "canvas-confetti";
import { api, getToken, getUser } from "@/lib/api";
import { downloadPdfWithProgress } from "@/lib/pdfDownload";
import { Lock, ArrowRight, Plus, FileText, Clock, CheckCircle2, GitCompare, ArrowLeft, Download, Loader2, Trash2, RotateCcw, ChevronDown, ChevronUp, Search, Pencil, X, Tag, Eye, Share2, MessageCircle, Send, Copy, Sparkles, Award, Bell, StickyNote, Users } from "lucide-react";
import { toast } from "sonner";

const CONSULTING_PAY_URL = "https://buy.stripe.com/dRm6oHdUJb7r0xy2QWgnK06";
const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Área privada de la Consultoría Premium.
 * Sin acceso → paywall. Con acceso → listado de consultorías + CTA para empezar una nueva.
 */
export default function Consulting({ embedded = false, expanded = true, onSummary }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [email, setEmail] = useState("");
  const [tests, setTests] = useState([]);
  const [compareMode, setCompareMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [downloadingId, setDownloadingId] = useState(null);
  const [discardingId, setDiscardingId] = useState(null);
  const [trashOpen, setTrashOpen] = useState(false);
  const [trashItems, setTrashItems] = useState([]);
  const [restoringId, setRestoringId] = useState(null);
  const [emptyingTrash, setEmptyingTrash] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | draft | ready
  const [editingId, setEditingId] = useState(null);
  const [editingNickname, setEditingNickname] = useState("");
  const [editingTagsInput, setEditingTagsInput] = useState("");
  const [savingMeta, setSavingMeta] = useState(false);
  const [previewingId, setPreviewingId] = useState(null); // {id, url}
  const [sharingTest, setSharingTest] = useState(null); // full test
  const [shareData, setShareData] = useState(null); // {public_url, share_token}
  const [shareEmails, setShareEmails] = useState("");
  const [shareMessage, setShareMessage] = useState("");
  const [shareSending, setShareSending] = useState(false);
  const [chatTest, setChatTest] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatSending, setChatSending] = useState(false);
  const [notesTest, setNotesTest] = useState(null);
  const [notesValue, setNotesValue] = useState("");
  const [notesSaving, setNotesSaving] = useState(false);
  const [collabTest, setCollabTest] = useState(null);
  const [collabList, setCollabList] = useState([]);
  const [collabNewEmails, setCollabNewEmails] = useState("");
  const [collabAdding, setCollabAdding] = useState(false);

  const readyTests = tests.filter((t) => t.status === "ready");
  const draftTests = tests.filter((t) => t.status === "draft");
  const canCompare = readyTests.length >= 2;

  // Métricas top
  const lastReadyDate = readyTests
    .map((t) => t.report_ready_at || t.updated_at || t.created_at)
    .filter(Boolean)
    .sort()
    .reverse()[0];
  const daysSinceLastReady = lastReadyDate
    ? Math.max(0, Math.floor((Date.now() - new Date(lastReadyDate).getTime()) / 86400000))
    : null;

  // Streak: consultorías completadas este año
  const currentYear = new Date().getFullYear();
  const thisYearCount = readyTests.filter((t) => {
    const d = t.report_ready_at || t.updated_at || t.created_at;
    return d && new Date(d).getFullYear() === currentYear;
  }).length;

  // Reminder inteligente: si >90 días sin consultoría nueva
  const showReminder = daysSinceLastReady !== null && daysSinceLastReady >= 90 && draftTests.length === 0;

  // Borradores siempre arriba (agrupados), resto por created_at desc como fallback local
  const sortedTests = [...tests].sort((a, b) => {
    const aDraft = a.status === "draft" ? 0 : 1;
    const bDraft = b.status === "draft" ? 0 : 1;
    if (aDraft !== bDraft) return aDraft - bDraft;
    const at = a.created_at ? new Date(a.created_at).getTime() : 0;
    const bt = b.created_at ? new Date(b.created_at).getTime() : 0;
    return bt - at;
  });

  // Lista filtrada por búsqueda y estado
  const q = search.trim().toLowerCase();
  const filteredTests = sortedTests.filter((t) => {
    if (statusFilter !== "all" && t.status !== statusFilter) return false;
    if (!q) return true;
    const hay = [t.company_name, t.nickname, ...(t.tags || [])].filter(Boolean).join(" ").toLowerCase();
    return hay.includes(q);
  });

  useEffect(() => {
    if (!onSummary || loading) return;
    onSummary({
      count: tests.length,
      drafts: tests.filter((t) => t.status === "draft").length,
      running: tests.filter((t) => t.status === "submitted" || t.status === "generating").length,
    });
  }, [tests, loading, onSummary]);

  async function loadTrash() {
    try {
      const r = await api.get("/consulting/tests/trash");
      setTrashItems(r.data.items || []);
    } catch {
      toast.error("No pudimos cargar la papelera");
    }
  }

  function toggleTrash() {
    setTrashOpen((prev) => !prev);
  }

  async function restoreTest(t) {
    if (restoringId) return;
    setRestoringId(t.id);
    try {
      await api.post(`/consulting/tests/${t.id}/restore`);
      setTrashItems((prev) => prev.filter((x) => x.id !== t.id));
      // Refresca lista principal
      try {
        const r = await api.get("/consulting/tests");
        setTests(r.data.items || []);
      } catch {}
      toast.success("Borrador restaurado");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No pudimos restaurar el borrador");
    } finally {
      setRestoringId(null);
    }
  }

  async function emptyTrash() {
    if (emptyingTrash) return;
    if (trashItems.length === 0) return;
    const ok = window.confirm(`¿Vaciar la papelera? Se eliminarán permanentemente ${trashItems.length} borrador${trashItems.length === 1 ? "" : "es"} descartado${trashItems.length === 1 ? "" : "s"}. Esta acción no se puede deshacer.`);
    if (!ok) return;
    setEmptyingTrash(true);
    try {
      const r = await api.delete("/consulting/tests/trash");
      setTrashItems([]);
      toast.success(`Papelera vaciada · ${r.data?.deleted ?? 0} eliminados`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No pudimos vaciar la papelera");
    } finally {
      setEmptyingTrash(false);
    }
  }

  async function discardTest(t, e) {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    if (discardingId) return;
    setDiscardingId(t.id);
    // Optimistic remove
    setTests((prev) => prev.filter((x) => x.id !== t.id));
    try {
      await api.delete(`/consulting/tests/${t.id}`);
      toast(`Test "${t.nickname || t.company_name || "Sin nombre"}" descartado`, {
        description: "Se ha enviado a la papelera.",
        action: {
          label: "Deshacer",
          onClick: async () => {
            try {
              await api.post(`/consulting/tests/${t.id}/restore`);
              const r = await api.get("/consulting/tests");
              setTests(r.data.items || []);
              const tr = await api.get("/consulting/tests/trash");
              setTrashItems(tr.data.items || []);
              toast.success("Test restaurado");
            } catch (err) {
              toast.error("No pudimos restaurar el test");
            }
          },
        },
        duration: 8000,
      });
      // Refresca papelera para actualizar contador
      try {
        const tr = await api.get("/consulting/tests/trash");
        setTrashItems(tr.data.items || []);
      } catch {}
    } catch (err) {
      // Si falla, restauramos en cliente
      setTests((prev) => [...prev, t]);
      toast.error(err?.response?.data?.detail || "No pudimos descartar el test");
    } finally {
      setDiscardingId(null);
    }
  }

  function openEditMeta(t) {
    setEditingId(t.id);
    setEditingNickname(t.nickname || "");
    setEditingTagsInput((t.tags || []).join(", "));
  }

  function closeEditMeta() {
    setEditingId(null);
    setEditingNickname("");
    setEditingTagsInput("");
  }

  async function saveMeta() {
    if (savingMeta) return;
    setSavingMeta(true);
    const tagsList = editingTagsInput
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .slice(0, 3);
    try {
      const { data } = await api.patch(`/consulting/tests/${editingId}/meta`, {
        nickname: editingNickname,
        tags: tagsList,
      });
      setTests((prev) => prev.map((x) => x.id === editingId ? { ...x, nickname: data.nickname, tags: data.tags } : x));
      toast.success("Datos actualizados");
      closeEditMeta();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No pudimos guardar los cambios");
    } finally {
      setSavingMeta(false);
    }
  }

  async function openPreview(t) {
    // Descarga el PDF con auth y crea un blob URL para el iframe.
    setPreviewingId({ id: t.id, url: null, loading: true });
    // Confetti si es la primera vez que ve este informe
    try {
      const seenKey = "thc_consulting_seen_reports";
      const seen = JSON.parse(localStorage.getItem(seenKey) || "[]");
      if (!seen.includes(t.id)) {
        confetti({
          particleCount: 80,
          spread: 70,
          origin: { y: 0.6 },
          colors: ["#0F6E56", "#C9A84C", "#E5C874", "#0b3d2e"],
          scalar: 0.9,
        });
        localStorage.setItem(seenKey, JSON.stringify([...seen, t.id].slice(-100)));
      }
    } catch {}
    try {
      const token = getToken();
      const resp = await fetch(`${API_BASE}/consulting/tests/${t.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error("failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      setPreviewingId({ id: t.id, url, loading: false });
    } catch {
      setPreviewingId(null);
      toast.error("No pudimos cargar la previsualización");
    }
  }

  function closePreview() {
    if (previewingId?.url) URL.revokeObjectURL(previewingId.url);
    setPreviewingId(null);
  }

  async function openShare(t) {
    setSharingTest(t);
    setShareData(null);
    setShareEmails("");
    setShareMessage("");
    try {
      const { data } = await api.post(`/consulting/tests/${t.id}/share`);
      setShareData({ public_url: data.public_url, share_token: data.share_token });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No pudimos generar el enlace");
      setSharingTest(null);
    }
  }

  function closeShare() {
    setSharingTest(null);
    setShareData(null);
    setShareEmails("");
    setShareMessage("");
  }

  async function revokeShare() {
    if (!sharingTest) return;
    if (!window.confirm("¿Revocar el enlace público? Quien lo tenga ya no podrá acceder.")) return;
    try {
      await api.delete(`/consulting/tests/${sharingTest.id}/share`);
      toast.success("Enlace revocado");
      closeShare();
    } catch {
      toast.error("No pudimos revocar el enlace");
    }
  }

  function copyShareUrl() {
    if (!shareData?.public_url) return;
    navigator.clipboard.writeText(shareData.public_url);
    toast.success("Enlace copiado al portapapeles");
  }

  async function sendShareEmails() {
    if (shareSending || !sharingTest) return;
    const emailsList = shareEmails.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
    if (emailsList.length === 0) {
      toast.error("Añade al menos un email");
      return;
    }
    setShareSending(true);
    try {
      const { data } = await api.post(`/consulting/tests/${sharingTest.id}/share/email`, {
        emails: emailsList,
        message: shareMessage,
      });
      toast.success(`Enviado a ${data.sent.length} destinatario(s)${data.failed.length ? ` · ${data.failed.length} fallo(s)` : ""}`);
      setShareEmails("");
      setShareMessage("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No pudimos enviar los emails");
    } finally {
      setShareSending(false);
    }
  }

  function openChat(t) {
    setChatTest(t);
    setChatMessages([]);
    setChatInput("");
  }

  function closeChat() {
    setChatTest(null);
    setChatMessages([]);
    setChatInput("");
  }

  async function sendChat() {
    if (chatSending || !chatTest) return;
    const q = chatInput.trim();
    if (!q) return;
    const userMsg = { role: "user", content: q };
    setChatMessages((prev) => [...prev, userMsg]);
    setChatInput("");
    setChatSending(true);
    try {
      const history = [...chatMessages];
      const { data } = await api.post(`/consulting/tests/${chatTest.id}/chat`, {
        question: q,
        history,
      });
      setChatMessages((prev) => [...prev, { role: "assistant", content: data.answer }]);
    } catch (err) {
      setChatMessages((prev) => [...prev, { role: "assistant", content: "❌ No pudimos generar la respuesta. Intenta de nuevo." }]);
    } finally {
      setChatSending(false);
    }
  }

  async function openNotes(t) {
    setNotesTest(t);
    setNotesValue(t.notes || "");
    try {
      const { data } = await api.get(`/consulting/tests/${t.id}/notes`);
      setNotesValue(data.notes || "");
    } catch {}
  }

  function closeNotes() {
    setNotesTest(null);
    setNotesValue("");
  }

  async function saveNotes() {
    if (notesSaving || !notesTest) return;
    setNotesSaving(true);
    try {
      await api.patch(`/consulting/tests/${notesTest.id}/meta`, { notes: notesValue });
      setTests((prev) => prev.map((x) => x.id === notesTest.id ? { ...x, notes: notesValue } : x));
      toast.success("Notas guardadas");
      closeNotes();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No pudimos guardar las notas");
    } finally {
      setNotesSaving(false);
    }
  }

  async function openCollab(t) {
    setCollabTest(t);
    setCollabList([]);
    setCollabNewEmails("");
    try {
      const { data } = await api.get(`/consulting/tests/${t.id}/collaborators`);
      setCollabList(data.collaborators || []);
    } catch {
      toast.error("No pudimos cargar la lista de colaboradores");
    }
  }

  function closeCollab() {
    setCollabTest(null);
    setCollabList([]);
    setCollabNewEmails("");
  }

  async function addCollab() {
    if (collabAdding) return;
    const emails = collabNewEmails.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
    if (emails.length === 0) { toast.error("Añade al menos un email"); return; }
    setCollabAdding(true);
    try {
      const { data } = await api.post(`/consulting/tests/${collabTest.id}/collaborators`, { emails });
      setCollabList(data.collaborators || []);
      setCollabNewEmails("");
      const msg = [];
      if (data.added?.length) msg.push(`${data.added.length} añadido(s)`);
      if (data.skipped?.length) msg.push(`${data.skipped.length} ya existía(n)`);
      if (data.invalid?.length) msg.push(`${data.invalid.length} inválido(s)`);
      toast.success(msg.join(" · ") || "Actualizado");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No pudimos añadir");
    } finally {
      setCollabAdding(false);
    }
  }

  async function removeCollab(email) {
    if (!window.confirm(`¿Revocar acceso de ${email}?`)) return;
    try {
      const { data } = await api.delete(`/consulting/tests/${collabTest.id}/collaborators/${encodeURIComponent(email)}`);
      setCollabList(data.collaborators || []);
      toast.success("Acceso revocado");
    } catch {
      toast.error("No pudimos revocar");
    }
  }

  async function downloadPdf(t, e) {
    if (e) e.stopPropagation();
    if (downloadingId) return;
    setDownloadingId(t.id);
    try {
      await downloadPdfWithProgress({
        url: `/consulting/tests/${t.id}/pdf`,
        filename: `Consultoria_${(t.company_name || "informe").replace(/\s+/g, "_")}.pdf`,
        kind: "consulting",
        label: `Preparando el informe de ${t.nickname || t.company_name || "consultoría"}`,
        variant: "premium",
        timeout: 300000,
      });
    } catch (err) {
      toast.error("No pudimos descargar el PDF");
    } finally {
      setDownloadingId(null);
    }
  }

  function toggleSelect(id) {
    setSelectedIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 5) {
        toast.error("Máximo 5 consultorías por comparativa");
        return prev;
      }
      return [...prev, id];
    });
  }
  function clearSelection() {
    setSelectedIds([]);
  }
  function launchCompare() {
    if (selectedIds.length < 2) {
      toast.error("Selecciona al menos 2 consultorías");
      return;
    }
    navigate(`/consulting/compare?ids=${selectedIds.join(",")}`);
  }
  async function bulkDiscard() {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    const ownedIds = ids.filter((id) => {
      const t = tests.find((x) => x.id === id);
      return t && t.is_owner !== false;
    });
    if (ownedIds.length === 0) {
      toast.error("Solo puedes enviar a papelera consultorías propias");
      return;
    }
    if (!window.confirm(`¿Enviar ${ownedIds.length} consultoría(s) a la papelera?`)) return;
    setSelectedIds([]);
    setTests((prev) => prev.filter((x) => !ownedIds.includes(x.id)));
    let ok = 0, fail = 0;
    await Promise.all(ownedIds.map(async (id) => {
      try { await api.delete(`/consulting/tests/${id}`); ok += 1; } catch { fail += 1; }
    }));
    try {
      const tr = await api.get("/consulting/tests/trash");
      setTrashItems(tr.data.items || []);
    } catch {}
    toast.success(`${ok} enviada(s) a papelera${fail ? ` · ${fail} error(es)` : ""}`);
  }

  useEffect(() => {
    const user = getUser();
    if (!user) {
      navigate("/login?next=/consulting");
      return;
    }
    setEmail(user.email || "");
    api
      .get("/consulting/status")
      .then(async (r) => {
        setHasAccess(!!r.data.has_consulting_access);
        if (r.data.has_consulting_access) {
          let loadedTests = [];
          try {
            const t = await api.get("/consulting/tests");
            loadedTests = t.data.items || [];
            setTests(loadedTests);
          } catch {}
          // Precarga la papelera para mostrar el contador aunque esté colapsada
          try {
            const tr = await api.get("/consulting/tests/trash");
            setTrashItems(tr.data.items || []);
          } catch {}
          // Deep-link: ?ask=<testId>&q=<pregunta> abre el chat con pregunta pre-cargada.
          try {
            const params = new URLSearchParams(window.location.search);
            const askId = params.get("ask");
            const askQ = params.get("q") || "";
            if (askId) {
              const target = loadedTests.find((x) => x.id === askId);
              if (target && target.status === "ready") {
                setChatTest(target);
                setChatMessages([]);
                setChatInput(askQ);
              }
              // limpiar querystring
              const url = new URL(window.location.href);
              url.searchParams.delete("ask");
              url.searchParams.delete("q");
              window.history.replaceState({}, "", url.toString());
            }
          } catch {}
        }
      })
      .catch(() => setHasAccess(false))
      .finally(() => setLoading(false));
  }, [navigate]);

  if (loading) {
    if (embedded) return <div className="text-sm text-slate-500 py-4">Cargando…</div>;
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-slate-500">Cargando…</div>
      </div>
    );
  }

  if (!hasAccess) {
    if (embedded) return null;
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6 py-16">
        <div className="max-w-xl w-full rounded-2xl bg-white border-2 shadow-xl p-10 text-center" style={{ borderColor: "#C9A84C" }} data-testid="consulting-paywall">
          <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-6" style={{ background: "#0F6E56" }}>
            <Lock className="w-7 h-7 text-white" strokeWidth={2} />
          </div>
          <h1 className="font-display text-3xl font-bold text-slate-900 mb-4">Consultoría de Gestión del Talento</h1>
          <p className="text-base leading-relaxed text-slate-700 mb-6">
            Análisis en profundidad de las 6 áreas críticas de gestión del talento aplicando
            nuestra metodología de consultoría. Recibes informe personalizado, recursos operativos
            adaptados a tu empresa y plan de acción escalonado a 30, 90 y 180 días.
          </p>
          <div className="rounded-xl p-4 mb-6" style={{ background: "#fef2f2", border: "1px solid #fecaca" }}>
            <p className="text-sm font-semibold text-red-700">Este servicio requiere el pago de €397.</p>
            <p className="text-sm text-red-600 mt-1">
              Tu email <span className="font-mono">{email}</span> no tiene acceso activo a la Consultoría de Gestión del Talento.
            </p>
          </div>
          <button
            onClick={async () => {
              try {
                const r = await api.post("/consulting/checkout-session");
                window.location.href = r.data.url;
              } catch (err) {
                toast.error(err?.response?.data?.detail || "No pudimos iniciar el checkout");
              }
            }}
            className="inline-flex items-center gap-2 rounded-full px-8 py-3 font-bold shadow-md hover:shadow-lg transition-shadow"
            style={{ background: "#0F6E56", color: "#C9A84C" }}
            data-testid="consulting-buy-btn"
          >
            Comprar acceso · €397 <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  const preview = embedded && !expanded;
  const listTests = preview ? filteredTests.slice(0, 2) : filteredTests;
  return (
    <div className={embedded ? "" : "min-h-screen bg-slate-50 py-12 px-6"} data-testid={embedded ? "consulting-embedded" : "consulting-dashboard"}>
      <div className={embedded ? "" : "max-w-4xl mx-auto"}>
        {!embedded && (<button
          onClick={() => navigate("/dashboard")}
          className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-900 mb-6 -mt-2 transition-colors"
          data-testid="consulting-back-to-dashboard-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Volver al dashboard
        </button>)}
        {!preview && (<div className={`flex items-end ${embedded ? "justify-start mb-4" : "justify-between mb-8"} flex-wrap gap-4`}>
          {!embedded && (<div>
            <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: "#0F6E56" }}>Área privada</div>
            <h1 className="font-display text-3xl font-bold text-slate-900 mt-1">Tus consultorías</h1>
          </div>)}
          <div className="flex gap-2 flex-wrap">
            <button
                onClick={() => navigate("/consulting/new")}
                className="group relative inline-flex items-center gap-2.5 rounded-full px-7 py-3 text-sm font-semibold text-white overflow-hidden transition-all duration-300 hover:-translate-y-0.5"
                style={{
                  background: "linear-gradient(135deg, #0b3d2e 0%, #14523f 55%, #0F6E56 100%)",
                  boxShadow: "0 10px 28px -10px rgba(11, 61, 46, 0.55), 0 0 0 1px rgba(201, 168, 76, 0.28) inset, 0 -1px 0 rgba(201, 168, 76, 0.35) inset",
                  letterSpacing: "0.02em",
                }}
                data-testid="consulting-new-btn"
              >
                <span
                  aria-hidden
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                  style={{
                    background: "radial-gradient(120% 60% at 50% 0%, rgba(201, 168, 76, 0.25) 0%, rgba(201, 168, 76, 0) 65%)",
                  }}
                />
                <span
                  aria-hidden
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: 999,
                    background: "linear-gradient(135deg, #C9A84C 0%, #E5C874 100%)",
                    color: "#0b3d2e",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 2px 6px -1px rgba(201,168,76,0.6)",
                  }}
                >
                  <Plus className="w-3.5 h-3.5" strokeWidth={2.5} />
                </span>
                <span className="relative">Empezar nueva consultoría</span>
              </button>
          </div>
        </div>)}

        {/* Bulk actions bar (sticky arriba cuando hay selección) */}
        {!preview && selectedIds.length > 0 && (
          <div
            className="sticky top-4 z-30 mb-4 rounded-2xl px-5 py-3 flex items-center gap-4 flex-wrap shadow-lg"
            style={{ background: "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 100%)", border: "1px solid rgba(201,168,76,0.4)" }}
            data-testid="consulting-bulk-bar"
          >
            <div className="flex items-center gap-2 text-white text-sm font-semibold flex-1 min-w-0">
              <span className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold" style={{ background: "linear-gradient(135deg, #C9A84C 0%, #E5C874 100%)", color: "#0b3d2e" }}>{selectedIds.length}</span>
              consultoría{selectedIds.length === 1 ? "" : "s"} seleccionada{selectedIds.length === 1 ? "" : "s"}
            </div>
            <button
              onClick={launchCompare}
              disabled={selectedIds.length < 2 || selectedIds.filter((id) => (tests.find((t) => t.id === id) || {}).status === "ready").length !== selectedIds.length}
              className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-semibold text-white border border-white/40 hover:bg-white/10 transition disabled:opacity-40 disabled:cursor-not-allowed"
              title="Selecciona 2-5 consultorías completadas"
              data-testid="consulting-bulk-compare"
            >
              <GitCompare className="w-3.5 h-3.5" /> Comparar
            </button>
            <button
              onClick={bulkDiscard}
              className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-semibold whitespace-nowrap"
              style={{ background: "linear-gradient(135deg, #b91c1c 0%, #dc2626 100%)", color: "#fff" }}
              data-testid="consulting-bulk-trash"
            >
              <Trash2 className="w-3.5 h-3.5" /> Enviar a papelera
            </button>
            <button
              onClick={clearSelection}
              className="text-xs text-white/70 hover:text-white"
              data-testid="consulting-bulk-clear"
            >
              Cancelar
            </button>
          </div>
        )}

        {/* Banner de reminder si >=90 días desde última consultoría */}
        {!preview && showReminder && (
          <div className="mb-4 rounded-2xl p-4 flex items-center gap-4 relative overflow-hidden" style={{ background: "linear-gradient(135deg, rgba(15,110,86,0.06) 0%, rgba(201,168,76,0.10) 100%)", border: "1px solid rgba(201,168,76,0.35)" }} data-testid="consulting-reminder-banner">
            <span aria-hidden className="absolute inset-x-8 top-0 h-px" style={{ background: "linear-gradient(90deg, transparent 0%, rgba(201,168,76,0.6) 50%, transparent 100%)" }} />
            <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "linear-gradient(135deg, #C9A84C 0%, #E5C874 100%)" }}>
              <Bell className="w-5 h-5" style={{ color: "#0b3d2e" }} strokeWidth={2.25} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-slate-900 text-sm">Hace {daysSinceLastReady} días que no revisas la salud del talento</div>
              <div className="text-xs text-slate-600 mt-0.5">Las mejores organizaciones repiten diagnóstico cada 3-6 meses para medir el impacto de las acciones.</div>
            </div>
            <button
              onClick={() => navigate("/consulting/new")}
              className="rounded-full px-4 py-2 text-xs font-semibold text-white flex-shrink-0"
              style={{ background: "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 100%)" }}
              data-testid="consulting-reminder-cta"
            >
              Nueva consultoría
            </button>
          </div>
        )}

        {/* Barra de métricas premium */}
        {!preview && tests.length > 0 && (
          <div className={`grid ${thisYearCount >= 3 ? "grid-cols-2 md:grid-cols-4" : "grid-cols-3"} gap-2 sm:gap-3 mb-6`} data-testid="consulting-metrics-bar">
            <MetricCard
              testid="metric-completed"
              icon={<CheckCircle2 className="w-4 h-4" style={{ color: "#0F6E56" }} strokeWidth={2.25} />}
              label="Completadas"
              value={readyTests.length}
            />
            <MetricCard
              testid="metric-drafts"
              icon={<FileText className="w-4 h-4 text-amber-500" strokeWidth={2.25} />}
              label="Borradores"
              value={draftTests.length}
            />
            <MetricCard
              testid="metric-last"
              icon={<Clock className="w-4 h-4 text-slate-500" strokeWidth={2.25} />}
              label="Última completada"
              value={
                daysSinceLastReady === null ? "—"
                  : daysSinceLastReady === 0 ? "Hoy"
                  : daysSinceLastReady === 1 ? "Ayer"
                  : `Hace ${daysSinceLastReady} d`
              }
            />
            {thisYearCount >= 3 && (
              <div
                className="rounded-2xl px-5 py-4 relative overflow-hidden"
                style={{ background: "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 100%)", border: "1px solid rgba(201,168,76,0.4)" }}
                data-testid="metric-streak"
              >
                <span aria-hidden className="absolute inset-x-6 top-0 h-px" style={{ background: "linear-gradient(90deg, transparent 0%, rgba(229,200,116,0.7) 50%, transparent 100%)" }} />
                <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest" style={{ color: "#E5C874" }}>
                  <Award className="w-4 h-4" strokeWidth={2.25} /> Streak {currentYear}
                </div>
                <div className="font-display text-2xl font-bold mt-1" style={{ color: "#E5C874" }}>
                  {thisYearCount} <span className="text-sm font-normal">consultorías</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Buscador + chips de filtro */}
        {!preview && tests.length > 0 && (
          <div className="mb-5 flex flex-wrap gap-3 items-center" data-testid="consulting-filters-bar">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Buscar por empresa, alias o tag…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-full border border-slate-200 bg-white pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-[#0F6E56]"
                data-testid="consulting-search-input"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {[
                { k: "all", l: `Todas (${tests.length})` },
                { k: "draft", l: `Borradores (${draftTests.length})` },
                { k: "ready", l: `Completadas (${readyTests.length})` },
              ].map((c) => {
                const active = statusFilter === c.k;
                return (
                  <button
                    key={c.k}
                    onClick={() => setStatusFilter(c.k)}
                    className={`rounded-full px-4 py-2 text-xs font-semibold transition-all ${active ? "text-white shadow" : "text-slate-600 hover:text-slate-900 bg-white border border-slate-200"}`}
                    style={active ? { background: "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 100%)" } : {}}
                    data-testid={`consulting-status-chip-${c.k}`}
                  >
                    {c.l}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {!preview && trashItems.length > 0 && (
          <div className="mb-6" data-testid="consulting-trash-section">
            <div className="w-full flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-4 sm:px-5 py-3.5">
              <button
                onClick={toggleTrash}
                className="flex-1 min-w-[150px] inline-flex items-center justify-between gap-2 text-sm font-semibold text-slate-700 hover:text-slate-900 transition"
                data-testid="consulting-trash-toggle"
              >
                <span className="inline-flex items-center gap-2">
                  <Trash2 className="w-4 h-4 text-slate-500" />
                  Papelera
                  <span className="text-[11px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ background: "linear-gradient(135deg, #C9A84C 0%, #E5C874 100%)", color: "#0b3d2e" }}>
                    {trashItems.length}
                  </span>
                  <span className="text-[11px] font-medium text-slate-400 hidden sm:inline">
                    · Se vacían automáticamente a los 30 días
                  </span>
                </span>
                {trashOpen ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
              </button>
              <button
                onClick={emptyTrash}
                disabled={emptyingTrash || trashItems.length === 0}
                className="shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 sm:px-4 py-1.5 text-xs font-semibold whitespace-nowrap transition-all duration-200 hover:-translate-y-0.5 disabled:opacity-50"
                style={{
                  background: "linear-gradient(135deg, #b91c1c 0%, #dc2626 55%, #b91c1c 100%)",
                  color: "#ffffff",
                  boxShadow: "0 4px 12px -6px rgba(185,28,28,0.55), inset 0 1px 0 rgba(255,255,255,0.15)",
                }}
                data-testid="consulting-empty-trash-btn"
                title="Eliminar permanentemente todos los borradores descartados"
              >
                {emptyingTrash ? <Loader2 className="w-3 h-3 animate-spin" strokeWidth={2.5} /> : <Trash2 className="w-3 h-3" strokeWidth={2.5} />}
                Vaciar<span className="hidden sm:inline"> papelera</span>
              </button>
            </div>
            {trashOpen && (
              <div className="mt-3 space-y-2" data-testid="consulting-trash-list">
                {trashItems.map((t) => (
                  <div
                    key={t.id}
                    className="rounded-2xl border border-slate-200 bg-slate-50/60 px-5 py-4 flex items-center justify-between gap-4"
                    data-testid="consulting-trash-row"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-lg bg-slate-200 flex items-center justify-center flex-shrink-0">
                        <Trash2 className="w-4 h-4 text-slate-500" />
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold text-slate-800 truncate">{t.company_name || "Sin nombre"}</div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          Descartado {t.trashed_at ? new Date(t.trashed_at).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" }) : ""}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => restoreTest(t)}
                      disabled={restoringId === t.id}
                      className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold text-white whitespace-nowrap transition-all duration-200 hover:-translate-y-0.5 disabled:opacity-60"
                      style={{
                        background: "linear-gradient(135deg, #0b3d2e 0%, #14523f 55%, #0F6E56 100%)",
                        boxShadow: "0 6px 18px -8px rgba(11,61,46,0.5), inset 0 0 0 1px rgba(201,168,76,0.28)",
                      }}
                      data-testid={`consulting-restore-btn-${t.id}`}
                    >
                      {restoringId === t.id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={2.5} />
                      ) : (
                        <RotateCcw className="w-3.5 h-3.5" strokeWidth={2.5} style={{ color: "#C9A84C" }} />
                      )}
                      Restaurar
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tests.length === 0 ? (
          <div className="rounded-3xl bg-white border border-slate-200 p-10 md:p-14 relative overflow-hidden" data-testid="consulting-empty-state">
            <div aria-hidden className="absolute -top-24 -right-24 w-64 h-64 rounded-full opacity-40" style={{ background: "radial-gradient(circle, rgba(201,168,76,0.35) 0%, transparent 70%)" }} />
            <div aria-hidden className="absolute -bottom-24 -left-24 w-64 h-64 rounded-full opacity-30" style={{ background: "radial-gradient(circle, rgba(15,110,86,0.35) 0%, transparent 70%)" }} />
            <div className="relative">
              <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 mb-4" style={{ background: "linear-gradient(135deg, rgba(15,110,86,0.08) 0%, rgba(201,168,76,0.14) 100%)", border: "1px solid rgba(201,168,76,0.4)" }}>
                <Sparkles className="w-3.5 h-3.5" style={{ color: "#0F6E56" }} strokeWidth={2.25} />
                <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: "#0F6E56" }}>Comienza aquí</span>
              </div>
              <h2 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mb-2">
                Diagnostica la salud del talento en <em style={{ color: "#0F6E56" }}>tu empresa</em>
              </h2>
              <p className="text-slate-600 mb-8 max-w-xl">
                Análisis profundo de 35 preguntas construido con nuestra metodología de consultoría. Informe personalizado, plan de acción y 7 recursos ejecutables listos para aplicar en tu empresa.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                {[
                  { n: 1, t: "Nueva consultoría", d: "Datos básicos de tu empresa en 2 min" },
                  { n: 2, t: "Cuestionario", d: "35 preguntas · guardado automático" },
                  { n: 3, t: "Informe en 24 h", d: "PDF · checklist · set operativo" },
                ].map((step) => (
                  <div key={step.n} className="rounded-2xl bg-white border border-slate-200 p-4 flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm" style={{ background: "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 100%)", color: "#E5C874" }}>
                      {step.n}
                    </div>
                    <div>
                      <div className="font-semibold text-slate-900 text-sm">{step.t}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{step.d}</div>
                    </div>
                  </div>
                ))}
              </div>

              <button
                onClick={() => navigate("/consulting/new")}
                className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold transition-all duration-300 hover:-translate-y-0.5"
                style={{ background: "linear-gradient(135deg, #C9A84C 0%, #E5C874 55%, #C9A84C 100%)", color: "#0b3d2e", boxShadow: "0 12px 26px -12px rgba(201,168,76,0.6)" }}
                data-testid="consulting-empty-cta"
              >
                <Plus className="w-4 h-4" strokeWidth={2.5} />
                Empezar mi primera consultoría
                <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {listTests.map((t) => {
              const isDraft = t.status === "draft";
              const isReady = t.status === "ready";
              const isGenerating = t.status === "generating";
              const isFailed = t.status === "generating_failed";
              const label = isDraft
                ? "Borrador · pendiente de enviar"
                : t.status === "submitted"
                ? "Enviado · esperando timer"
                : isGenerating
                ? "Informe en proceso de análisis…"
                : isReady
                ? "Informe listo"
                : isFailed
                ? "Error al generar — reintenta"
                : t.status;
              const target = isDraft
                ? `/consulting/test/${t.id}`
                : (isReady || isGenerating || isFailed)
                ? `/consulting/${t.id}/report`
                : `/consulting/${t.id}`;
              const isSelected = selectedIds.includes(t.id);
              const canSelect = t.is_owner !== false && (isReady || isDraft);
              return (
                <div
                  key={t.id}
                  className={`group relative rounded-2xl p-4 md:p-5 overflow-hidden flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 lg:gap-4 transition-all duration-300 hover:-translate-y-0.5 cursor-pointer ${isGenerating ? "generating-pulse" : ""}`}
                  style={
                    isSelected
                      ? {
                          background: "linear-gradient(135deg, rgba(15,110,86,0.08) 0%, rgba(201,168,76,0.06) 100%), linear-gradient(180deg, #ffffff 0%, #fdfcf6 100%)",
                          boxShadow: "0 22px 45px -25px rgba(15,110,86,0.32), 0 0 0 1.5px rgba(201,168,76,0.55)",
                        }
                      : isDraft
                      ? {
                          background: "linear-gradient(135deg, #fff7ed 0%, #fefce8 100%)",
                          boxShadow: "0 22px 45px -25px rgba(249,115,22,0.28), 0 0 0 1.5px rgba(253,186,116,0.6)",
                        }
                      : {
                          background: "linear-gradient(180deg, #ffffff 0%, #fdfcf6 100%)",
                          boxShadow: "0 22px 45px -30px rgba(11,61,46,0.28), 0 0 0 1px rgba(11,61,46,0.06)",
                        }
                  }
                  onClick={() => navigate(target)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate(target); } }}
                  title="Abrir informe"
                  data-testid="consulting-test-row"
                >
                  {/* Spine vertical por estado */}
                  <span
                    aria-hidden
                    className="absolute top-0 bottom-0 left-0 w-1 transition-all duration-300 group-hover:w-1.5"
                    style={{
                      background: isSelected
                        ? "linear-gradient(180deg, #C9A84C 0%, #0F6E56 100%)"
                        : isDraft
                        ? "linear-gradient(180deg, #f97316 0%, #f59e0b 100%)"
                        : isReady
                        ? "linear-gradient(180deg, #0F6E56 0%, #0F6E5680 100%)"
                        : "linear-gradient(180deg, #64748b 0%, #64748b80 100%)",
                    }}
                  />
                  {/* Barra dorada en hover */}
                  <span
                    aria-hidden
                    className="absolute top-0 left-1 right-0 h-[2px] opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ background: "linear-gradient(90deg, #C9A84C 0%, #E5C874 50%, #C9A84C 100%)" }}
                  />
                  <div className="flex items-center gap-4 min-w-0">
                    {canSelect && !preview && (
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelect(t.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-5 h-5 accent-emerald-600 flex-shrink-0"
                        data-testid={`consulting-select-checkbox-${t.id}`}
                      />
                    )}
                    <div
                      className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 transition-all duration-300 group-hover:scale-[1.05] group-hover:rotate-[-2deg]"
                      style={{
                        background: isDraft
                          ? "linear-gradient(135deg, #f97316 0%, #f59e0b 100%)"
                          : isReady
                          ? "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)"
                          : "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)",
                        boxShadow: isDraft
                          ? "0 10px 24px -8px rgba(249,115,22,0.55), inset 0 -1px 0 rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.35)"
                          : isReady
                          ? "0 10px 24px -8px rgba(15,110,86,0.35), inset 0 -1px 0 rgba(255,255,255,0.35), inset 0 1px 0 rgba(255,255,255,0.6)"
                          : "0 6px 14px -6px rgba(11,61,46,0.15), inset 0 1px 0 rgba(255,255,255,0.6)",
                        border: isReady ? "1px solid rgba(15,110,86,0.20)" : "none",
                      }}
                    >
                      {isReady ? <CheckCircle2 className="w-5 h-5" style={{ color: "#0F6E56" }} strokeWidth={2.25} /> : isDraft ? <FileText className="w-5 h-5 text-white" strokeWidth={2.25} /> : <Clock className="w-5 h-5" style={{ color: "#64748b" }} strokeWidth={2} />}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-900 truncate">{t.nickname || t.company_name || "Sin nombre"}</span>
                        {t.nickname && t.company_name && (
                          <span className="text-[11px] text-slate-500 truncate">· {t.company_name}</span>
                        )}
                        {isDraft && (
                          <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 border border-orange-200" data-testid="consulting-draft-badge">
                            Test no completado
                          </span>
                        )}
                        {t.is_owner === false && (
                          <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ background: "rgba(15,110,86,0.10)", color: "#0F6E56", border: "1px solid rgba(15,110,86,0.25)" }} data-testid="consulting-shared-badge" title="Este informe es de otra persona que te dio acceso">
                            Compartida contigo
                          </span>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); openEditMeta(t); }}
                          className="text-slate-400 hover:text-[#0F6E56] transition-colors"
                          title="Renombrar y taggear"
                          data-testid={`consulting-edit-meta-${t.id}`}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      {(t.tags || []).length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {(t.tags || []).map((tg) => (
                            <span key={tg} className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "rgba(15,110,86,0.08)", color: "#0F6E56", border: "1px solid rgba(15,110,86,0.2)" }}>
                              #{tg}
                            </span>
                          ))}
                        </div>
                      )}
                      <div className={`text-xs mt-1 ${isDraft ? "text-orange-700 font-medium" : "text-slate-500"}`}>{label}</div>
                    </div>
                  </div>
                  {isDraft ? (
                      <div className="flex items-center gap-2 flex-wrap">
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(target); }}
                          className="rounded-full px-4 py-2 text-sm font-semibold whitespace-nowrap shadow"
                          style={{ background: "linear-gradient(90deg, #f97316, #f59e0b)", color: "white" }}
                          data-testid="consulting-continue-draft-btn"
                        >
                          Continuar test
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5 md:gap-2 flex-wrap w-full lg:w-auto justify-start lg:justify-end">
                        {isReady && (
                          <>
                            <button
                              onClick={(e) => { e.stopPropagation(); openPreview(t); }}
                              className="inline-flex items-center gap-1.5 rounded-full px-2.5 md:px-3 py-2 text-xs font-semibold text-slate-600 border border-slate-200 hover:border-[#0F6E56] hover:text-[#0F6E56] transition"
                              data-testid={`consulting-preview-btn-${t.id}`}
                              title="Previsualizar el PDF antes de descargar"
                            >
                              <Eye className="w-3.5 h-3.5" /> <span className="hidden lg:inline">Preview</span>
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); openChat(t); }}
                              className="inline-flex items-center gap-1.5 rounded-full px-2.5 md:px-3 py-2 text-xs font-semibold text-slate-600 border border-slate-200 hover:border-[#0F6E56] hover:text-[#0F6E56] transition"
                              data-testid={`consulting-chat-btn-${t.id}`}
                              title="Consultar el informe"
                            >
                              <MessageCircle className="w-3.5 h-3.5" /> <span className="hidden lg:inline">Preguntar</span>
                            </button>
                            {t.is_owner !== false && (
                              <>
                                <button
                                  onClick={(e) => { e.stopPropagation(); openNotes(t); }}
                                  className="inline-flex items-center gap-1.5 rounded-full px-2.5 md:px-3 py-2 text-xs font-semibold text-slate-600 border border-slate-200 hover:border-[#0F6E56] hover:text-[#0F6E56] transition relative"
                                  data-testid={`consulting-notes-btn-${t.id}`}
                                  title="Notas privadas del propietario"
                                >
                                  <StickyNote className="w-3.5 h-3.5" /> <span className="hidden lg:inline">Notas</span>
                                  {t.notes && <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full" style={{ background: "#C9A84C" }} />}
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); openCollab(t); }}
                                  className="inline-flex items-center gap-1.5 rounded-full px-2.5 md:px-3 py-2 text-xs font-semibold text-slate-600 border border-slate-200 hover:border-[#0F6E56] hover:text-[#0F6E56] transition"
                                  data-testid={`consulting-collab-btn-${t.id}`}
                                  title="Gestionar colaboradores"
                                >
                                  <Users className="w-3.5 h-3.5" /> <span className="hidden lg:inline">Equipo</span>
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); openShare(t); }}
                                  className="inline-flex items-center gap-1.5 rounded-full px-2.5 md:px-3 py-2 text-xs font-semibold text-slate-600 border border-slate-200 hover:border-[#0F6E56] hover:text-[#0F6E56] transition"
                                  data-testid={`consulting-share-btn-${t.id}`}
                                  title="Compartir link público"
                                >
                                  <Share2 className="w-3.5 h-3.5" /> <span className="hidden lg:inline">Compartir</span>
                                </button>
                              </>
                            )}
                          </>
                        )}
                        {isReady && (
                          <button
                            onClick={(e) => downloadPdf(t, e)}
                            disabled={downloadingId === t.id}
                            className="group relative inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold whitespace-nowrap overflow-hidden transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-70"
                            style={{
                              background: "linear-gradient(135deg, #C9A84C 0%, #E5C874 55%, #C9A84C 100%)",
                              color: "#0b3d2e",
                              boxShadow: "0 6px 18px -6px rgba(201,168,76,0.55), inset 0 -1px 0 rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.4)",
                              letterSpacing: "0.02em",
                            }}
                            data-testid="consulting-download-pdf-btn"
                            title="Descargar PDF del informe completo"
                          >
                            {downloadingId === t.id ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={2.5} />
                            ) : (
                              <Download className="w-3.5 h-3.5" strokeWidth={2.5} />
                            )}
                            <span className="relative">{downloadingId === t.id ? "Descargando…" : "Descargar PDF"}</span>
                          </button>
                        )}
                        <button
                          onClick={() => navigate(target)}
                          className="group relative inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold text-white whitespace-nowrap overflow-hidden transition-all duration-300 hover:-translate-y-0.5"
                          style={{
                            background: "linear-gradient(135deg, #0b3d2e 0%, #14523f 55%, #0F6E56 100%)",
                            boxShadow: "0 6px 18px -8px rgba(11, 61, 46, 0.5), 0 0 0 1px rgba(201, 168, 76, 0.25) inset",
                            letterSpacing: "0.02em",
                          }}
                          data-testid="consulting-view-btn"
                        >
                          <span
                            aria-hidden
                            className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                            style={{
                              background: "radial-gradient(120% 60% at 50% 0%, rgba(201, 168, 76, 0.22) 0%, rgba(201, 168, 76, 0) 65%)",
                            }}
                          />
                          <span className="relative">Ver</span>
                          <ArrowRight className="relative w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5" style={{ color: "#C9A84C" }} strokeWidth={2.5} />
                        </button>
                      </div>
                    )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal de edición meta (nickname + tags) */}
      {editingId && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/50 backdrop-blur-sm"
          onClick={closeEditMeta}
          data-testid="consulting-edit-meta-modal"
        >
          <div className="max-w-md w-full rounded-2xl bg-white shadow-2xl p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-xs uppercase tracking-widest mb-1" style={{ color: "#0F6E56" }}>Editar</div>
                <h3 className="font-display text-xl font-bold text-slate-900">Alias y etiquetas</h3>
              </div>
              <button onClick={closeEditMeta} className="text-slate-400 hover:text-slate-900"><X className="w-5 h-5" /></button>
            </div>
            <label className="block text-xs font-semibold text-slate-700 mb-1.5">Alias (opcional)</label>
            <input
              type="text"
              value={editingNickname}
              onChange={(e) => setEditingNickname(e.target.value.slice(0, 60))}
              placeholder="Ej. Q4 2025 · Ampliación equipo"
              maxLength={60}
              className="w-full rounded-lg border border-slate-200 px-4 py-2.5 text-sm mb-1 focus:outline-none focus:border-[#0F6E56]"
              data-testid="consulting-meta-nickname"
            />
            <div className="text-[11px] text-slate-400 mb-4 text-right">{editingNickname.length}/60</div>
            <label className="block text-xs font-semibold text-slate-700 mb-1.5 flex items-center gap-1.5">
              <Tag className="w-3.5 h-3.5" /> Etiquetas (hasta 3, separadas por coma)
            </label>
            <input
              type="text"
              value={editingTagsInput}
              onChange={(e) => setEditingTagsInput(e.target.value)}
              placeholder="Ej. RRHH, 2026, España"
              className="w-full rounded-lg border border-slate-200 px-4 py-2.5 text-sm mb-4 focus:outline-none focus:border-[#0F6E56]"
              data-testid="consulting-meta-tags"
            />
            <div className="flex gap-2 mt-2">
              <button
                onClick={closeEditMeta}
                className="flex-1 rounded-full py-2.5 text-sm font-semibold text-slate-600 border border-slate-200 hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                onClick={saveMeta}
                disabled={savingMeta}
                className="flex-1 rounded-full py-2.5 text-sm font-semibold text-white disabled:opacity-60"
                style={{ background: "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 100%)" }}
                data-testid="consulting-meta-save"
              >
                {savingMeta ? "Guardando…" : "Guardar"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Preview PDF inline */}
      {previewingId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/60 backdrop-blur-sm" onClick={closePreview} data-testid="consulting-preview-modal">
          <div className="max-w-5xl w-full h-[90vh] rounded-2xl bg-white shadow-2xl flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h3 className="font-display text-lg font-bold text-slate-900">Vista previa del informe</h3>
              <button onClick={closePreview} className="text-slate-400 hover:text-slate-900"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 bg-slate-100">
              {previewingId.loading || !previewingId.url ? (
                <div className="h-full flex items-center justify-center gap-2 text-slate-500">
                  <Loader2 className="w-5 h-5 animate-spin" /> Preparando PDF…
                </div>
              ) : (
                <iframe src={previewingId.url} className="w-full h-full" title="Preview PDF" />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Compartir informe */}
      {sharingTest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/50 backdrop-blur-sm" onClick={closeShare} data-testid="consulting-share-modal">
          <div className="max-w-md w-full rounded-2xl bg-white shadow-2xl p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-xs uppercase tracking-widest mb-1" style={{ color: "#0F6E56" }}>Compartir</div>
                <h3 className="font-display text-xl font-bold text-slate-900">{sharingTest.nickname || sharingTest.company_name}</h3>
              </div>
              <button onClick={closeShare} className="text-slate-400 hover:text-slate-900"><X className="w-5 h-5" /></button>
            </div>
            <label className="block text-xs font-semibold text-slate-700 mb-1.5">Enlace público (read-only)</label>
            <div className="flex gap-2 mb-4">
              <input
                type="text"
                readOnly
                value={shareData?.public_url || "Generando…"}
                className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-xs font-mono text-slate-700 bg-slate-50"
                data-testid="consulting-share-url"
              />
              <button
                onClick={copyShareUrl}
                disabled={!shareData}
                className="rounded-lg px-3 py-2 text-xs font-semibold text-white disabled:opacity-50"
                style={{ background: "#0F6E56" }}
                data-testid="consulting-share-copy-btn"
              >
                <Copy className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="border-t border-slate-100 pt-4">
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">Enviar por email (máx 5, separados por coma)</label>
              <input
                type="text"
                value={shareEmails}
                onChange={(e) => setShareEmails(e.target.value)}
                placeholder="rrhh@empresa.com, direccion@empresa.com"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm mb-2"
                data-testid="consulting-share-emails"
              />
              <textarea
                value={shareMessage}
                onChange={(e) => setShareMessage(e.target.value.slice(0, 500))}
                placeholder="Mensaje opcional (500 caracteres)"
                rows={2}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm mb-3 resize-none"
                data-testid="consulting-share-message"
              />
              <button
                onClick={sendShareEmails}
                disabled={shareSending || !shareData}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full py-2.5 text-sm font-semibold text-white disabled:opacity-60"
                style={{ background: "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 100%)" }}
                data-testid="consulting-share-send-btn"
              >
                {shareSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Enviar
              </button>
            </div>
            <button
              onClick={revokeShare}
              className="w-full mt-4 text-xs text-red-600 hover:underline"
              data-testid="consulting-share-revoke-btn"
            >
              Revocar el enlace público
            </button>
          </div>
        </div>
      )}

      {/* Chat contextual */}
      {chatTest && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={closeChat} data-testid="consulting-chat-panel">
          <div className="w-full max-w-lg h-full bg-white shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between" style={{ background: "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 100%)" }}>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: "#E5C874" }}>Pregunta al informe</div>
                <div className="text-white font-semibold text-sm mt-0.5 truncate">{chatTest.nickname || chatTest.company_name}</div>
              </div>
              <button onClick={closeChat} className="text-white/70 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {chatMessages.length === 0 && (
                <div className="text-center text-sm text-slate-500 mt-8">
                  <MessageCircle className="w-8 h-8 mx-auto mb-3 text-slate-300" />
                  Haz una pregunta sobre tu informe.
                  <div className="mt-4 space-y-2">
                    {[
                      "¿Cuál es la acción más urgente que debo priorizar?",
                      "Resume el informe en 3 frases para mi CEO",
                      "¿Qué debería hacer en los primeros 30 días?",
                    ].map((s) => (
                      <button
                        key={s}
                        onClick={() => setChatInput(s)}
                        className="w-full text-left px-3 py-2 text-xs rounded-lg bg-slate-50 border border-slate-200 hover:border-[#0F6E56] text-slate-700"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {chatMessages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${m.role === "user" ? "text-white" : "bg-slate-100 text-slate-800"}`}
                    style={m.role === "user" ? { background: "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 100%)" } : {}}
                  >
                    {m.content}
                  </div>
                </div>
              ))}
              {chatSending && (
                <div className="flex justify-start">
                  <div className="rounded-2xl px-4 py-2.5 bg-slate-100 text-slate-500 text-sm inline-flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Pensando…
                  </div>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-slate-200 flex gap-2">
              <input
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendChat(); } }}
                placeholder="Escribe tu pregunta…"
                maxLength={800}
                className="flex-1 rounded-full border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:border-[#0F6E56]"
                data-testid="consulting-chat-input"
              />
              <button
                onClick={sendChat}
                disabled={chatSending || !chatInput.trim()}
                className="rounded-full px-4 py-2.5 text-white disabled:opacity-50"
                style={{ background: "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 100%)" }}
                data-testid="consulting-chat-send-btn"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Modal Notas privadas */}
      {notesTest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/50 backdrop-blur-sm" onClick={closeNotes} data-testid="consulting-notes-modal">
          <div className="max-w-lg w-full rounded-2xl bg-white shadow-2xl p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-xs uppercase tracking-widest mb-1" style={{ color: "#0F6E56" }}>Notas privadas</div>
                <h3 className="font-display text-xl font-bold text-slate-900">{notesTest.nickname || notesTest.company_name}</h3>
                <p className="text-xs text-slate-500 mt-1">Solo tú puedes ver este contenido. Ideal para hallazgos, decisiones y seguimiento.</p>
              </div>
              <button onClick={closeNotes} className="text-slate-400 hover:text-slate-900"><X className="w-5 h-5" /></button>
            </div>
            <textarea
              value={notesValue}
              onChange={(e) => setNotesValue(e.target.value.slice(0, 5000))}
              placeholder="Ej. Compartí el informe con dirección el 15/03. Priorizamos rediseñar entrevistas antes de Q2. Falta revisar cómo medir el impacto en NPS interno."
              rows={9}
              className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm mb-2 focus:outline-none focus:border-[#0F6E56] resize-none"
              data-testid="consulting-notes-textarea"
            />
            <div className="text-[11px] text-slate-400 mb-4 text-right">{notesValue.length}/5000</div>
            <div className="flex gap-2">
              <button onClick={closeNotes} className="flex-1 rounded-full py-2.5 text-sm font-semibold text-slate-600 border border-slate-200 hover:bg-slate-50">Cancelar</button>
              <button
                onClick={saveNotes}
                disabled={notesSaving}
                className="flex-1 rounded-full py-2.5 text-sm font-semibold text-white disabled:opacity-60"
                style={{ background: "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 100%)" }}
                data-testid="consulting-notes-save"
              >
                {notesSaving ? "Guardando…" : "Guardar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Colaboradores */}
      {collabTest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/50 backdrop-blur-sm" onClick={closeCollab} data-testid="consulting-collab-modal">
          <div className="max-w-md w-full rounded-2xl bg-white shadow-2xl p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-xs uppercase tracking-widest mb-1" style={{ color: "#0F6E56" }}>Equipo</div>
                <h3 className="font-display text-xl font-bold text-slate-900">Colaboradores</h3>
                <p className="text-xs text-slate-500 mt-1">Añade emails de tu equipo para darles acceso lectura al informe, checklist y herramientas.</p>
              </div>
              <button onClick={closeCollab} className="text-slate-400 hover:text-slate-900"><X className="w-5 h-5" /></button>
            </div>
            <div className="mb-4">
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">Añadir emails (separados por coma)</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={collabNewEmails}
                  onChange={(e) => setCollabNewEmails(e.target.value)}
                  placeholder="rrhh@empresa.com, direccion@empresa.com"
                  className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  data-testid="consulting-collab-input"
                />
                <button
                  onClick={addCollab}
                  disabled={collabAdding}
                  className="rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                  style={{ background: "#0F6E56" }}
                  data-testid="consulting-collab-add"
                >
                  {collabAdding ? "…" : "Añadir"}
                </button>
              </div>
              <p className="text-[11px] text-slate-400 mt-1.5">Necesitan cuenta en Talent Health Check para acceder. Reciben email de invitación.</p>
            </div>
            <div className="border-t border-slate-100 pt-4">
              <div className="text-xs font-semibold text-slate-700 mb-2">Con acceso ({collabList.length})</div>
              {collabList.length === 0 ? (
                <div className="text-sm text-slate-400 py-6 text-center">Nadie más tiene acceso todavía.</div>
              ) : (
                <div className="space-y-2">
                  {collabList.map((em) => (
                    <div key={em} className="flex items-center justify-between rounded-lg bg-slate-50 border border-slate-200 px-3 py-2" data-testid="consulting-collab-row">
                      <span className="text-sm text-slate-700 truncate">{em}</span>
                      <button onClick={() => removeCollab(em)} className="text-xs text-red-600 hover:underline flex-shrink-0 ml-2">Revocar</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MetricCard({ testid, icon, label, value }) {
  return (
    <div className="min-w-0 rounded-2xl bg-white border border-slate-200 px-2.5 py-3 sm:px-5 sm:py-4" data-testid={testid}>
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-1 sm:gap-2 text-[9px] sm:text-[10px] font-bold uppercase tracking-normal sm:tracking-widest text-slate-500">
        <span className="flex-shrink-0">{icon}</span>
        <span className="min-w-0 leading-tight [overflow-wrap:anywhere]">{label}</span>
      </div>
      <div className="font-display text-xl sm:text-2xl font-bold text-slate-900 mt-1 truncate">{value}</div>
    </div>
  );
}
